
import { defineComponent, ref, computed, watch } from 'vue';
import { message } from 'ant-design-vue';
import { OioModal, OioInput, OioInputNumber, OioButton } from '@kunlun/vue-ui-antd';
import { BillingBill, BillingBillLine, SpecifiedInvoiceContentItem } from '../types';
import { specifyInvoice } from '../api/billingBill';
import { SPECIFIABLE_LINE_STATUSES } from '../constants';

/** 可编辑行模型 */
interface ContentRow {
  specifiedInvoiceContent: string;
  quantity: number | undefined;
  amount: number | undefined;
  taxAmount: number | undefined;
  taxRate: number | undefined;
  unit: string;
  specModel: string;
  taxCode: string;
  taxCategoryName: string;
}

const emptyRow = (): ContentRow => ({
  specifiedInvoiceContent: '',
  quantity: undefined,
  amount: undefined,
  taxAmount: undefined,
  taxRate: undefined,
  unit: '',
  specModel: '',
  taxCode: '',
  taxCategoryName: ''
});

const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

export default defineComponent({
  name: 'SpecifyInvoiceDialog',
  components: { OioModal, OioInput, OioInputNumber, OioButton },
  props: {
    visible: { type: Boolean, default: false },
    selectedLines: { type: Array as () => BillingBillLine[], default: () => [] },
    billingBill: { type: Object as () => Partial<BillingBill>, default: () => ({}) },
    // 抽屉页当前开票信息选择（billingType/remark/invoiceInfoId/resetInvoiceInfo/deliveryAddress/noticeInvoiceNo），与部分开票同口径
    invoiceSelection: { type: Object as () => Record<string, any>, default: () => ({}) },
    // 草稿模式：独立开票创建向导复用本弹窗的行编辑与校验能力，但向导阶段尚未生成开票单（无 billId），
    // 校验通过后只回传指定开票内容，由父组件随创建入参一并提交，不调 specifyInvoice
    draft: { type: Boolean, default: false }
  },
  emits: ['update:visible', 'success', 'draftConfirm'],
  setup(props, { emit }) {
    const submitLoading = ref(false);
    const rows = ref<ContentRow[]>([emptyRow()]);

    const dialogVisible = computed({
      get: () => props.visible,
      set: (val) => emit('update:visible', val)
    });

    // 所选80单的总金额（行不含税净额合计）与总税额（单行销项税额合计）
    // 注意：接口返回的金额字段可能为字符串，必须 Number() 转换后再累加，避免字符串拼接导致 NaN
    const totalNetAmount = computed(() =>
      round2(props.selectedLines.reduce((sum, line) => sum + (Number(line.billingNetAmount) || 0), 0))
    );
    const totalTaxAmount = computed(() =>
      round2(props.selectedLines.reduce((sum, line) => sum + (Number(line.billingTaxAmount) || 0), 0))
    );

    // 已填写行的金额/税额合计
    // 注意：oio-input-number 在未完整输入时模型值可能为字符串（如 "35309."），必须 Number() 转换后再累加，避免字符串拼接导致 NaN
    const sumAmount = computed(() => round2(rows.value.reduce((sum, r) => sum + (Number(r.amount) || 0), 0)));
    const sumTaxAmount = computed(() => round2(rows.value.reduce((sum, r) => sum + (Number(r.taxAmount) || 0), 0)));

    const formatMoney = (val: number | null | undefined) =>
      val == null ? '0.00' : val.toFixed(2);

    // 注意：vxe-table 的 data 监听引用变化，原地 push/splice 不会刷新表格，必须整体替换数组
    const addRow = () => {
      rows.value = [...rows.value, emptyRow()];
    };

    const removeRow = (index: number) => {
      rows.value = rows.value.filter((_, i) => i !== index);
    };

    const validate = (): boolean => {
      if (props.selectedLines.length === 0) {
        message.warning('请先选择关联单据');
        return false;
      }
      // 校验行状态：仅 UNPACKED/CANCELLED/INVOICE_FAILED 可发起指定开票（开票失败可直接重发）
      const invalidLines = props.selectedLines.filter(
        (line) => !SPECIFIABLE_LINE_STATUSES.includes(line.status)
      );
      if (invalidLines.length > 0) {
        message.warning('仅可选择“未打包”、“已取消”或“开票失败”状态的单据进行指定开票');
        return false;
      }
      if (rows.value.length === 0) {
        message.warning('请至少填写一行指定开票内容');
        return false;
      }
      for (let i = 0; i < rows.value.length; i++) {
        const row = rows.value[i];
        const rowNo = i + 1;
        if (!row.specifiedInvoiceContent?.trim()) {
          message.warning(`第${rowNo}行：请输入指定开票内容`);
          return false;
        }
        if (row.quantity == null || Number.isNaN(Number(row.quantity))) {
          message.warning(`第${rowNo}行：请输入数量`);
          return false;
        }
        if (row.amount == null || Number.isNaN(Number(row.amount))) {
          message.warning(`第${rowNo}行：请输入金额`);
          return false;
        }
        if (row.taxAmount == null || Number.isNaN(Number(row.taxAmount))) {
          message.warning(`第${rowNo}行：请输入税额`);
          return false;
        }
        if (row.taxRate == null || Number.isNaN(Number(row.taxRate)) || Number(row.taxRate) < 0) {
          message.warning(`第${rowNo}行：请输入有效的税率`);
          return false;
        }
        if (!row.unit?.trim()) {
          message.warning(`第${rowNo}行：请输入单位`);
          return false;
        }
        if (!row.taxCode?.trim()) {
          message.warning(`第${rowNo}行：请输入税收分类编码`);
          return false;
        }
      }
      // 校验金额列总和、税额列总和与所选80单的总金额、总税额一致
      if (Math.abs(sumAmount.value - totalNetAmount.value) > 0.01) {
        message.error(
          `金额列总和(${formatMoney(sumAmount.value)})与所选80单总金额(${formatMoney(totalNetAmount.value)})不一致`
        );
        return false;
      }
      if (Math.abs(sumTaxAmount.value - totalTaxAmount.value) > 0.01) {
        message.error(
          `税额列总和(${formatMoney(sumTaxAmount.value)})与所选80单总税额(${formatMoney(totalTaxAmount.value)})不一致`
        );
        return false;
      }
      return true;
    };

    const handleSubmit = async () => {
      if (!validate()) return;
      // 组装指定开票内容（多行，行项目号从1递增）
      const contentItems: SpecifiedInvoiceContentItem[] = rows.value.map((row, i) => ({
        lineItemNo: i + 1,
        specifiedInvoiceContent: row.specifiedInvoiceContent,
        quantity: Number(row.quantity),
        amount: Number(row.amount),
        taxAmount: Number(row.taxAmount),
        taxRate: Number(row.taxRate),
        unit: row.unit,
        specModel: row.specModel,
        taxCode: row.taxCode,
        taxCategoryName: row.taxCategoryName
      }));
      if (props.draft) {
        emit('draftConfirm', contentItems);
        dialogVisible.value = false;
        return;
      }
      submitLoading.value = true;
      try {
        // 调用后端 SpecifyInvoice Action（指定开票内容仅本次打包使用，不落库，打包时以数组传入 ZDKPNR）
        // 按勾选行打包 + 保存当前开票信息选择，口径与部分开票一致
        const billId = props.billingBill!.id as number;
        const lineIds = props.selectedLines.map(l => l.id as number);
        await specifyInvoice(billId, contentItems, { ...props.invoiceSelection, partPackLineIds: lineIds });
        message.success('指定开票已提交');
        dialogVisible.value = false;
        emit('success');
      } catch (e: any) {
        message.error(e?.message || '指定开票失败');
      } finally {
        submitLoading.value = false;
      }
    };

    const handleCancel = () => {
      dialogVisible.value = false;
    };

    const resetForm = () => {
      rows.value = [emptyRow()];
    };

    // 弹窗打开时重置行数据
    watch(
      () => props.visible,
      (val: boolean) => {
        if (val) resetForm();
      }
    );

    return {
      dialogVisible,
      submitLoading,
      rows,
      totalNetAmount,
      totalTaxAmount,
      sumAmount,
      sumTaxAmount,
      formatMoney,
      addRow,
      removeRow,
      handleSubmit,
      handleCancel
    };
  }
});
